import cuid from "cuid";
import dayjs from "dayjs";
import { get, isEmpty } from "lodash";
import { useMemo } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import {
  hundredYearsAgoStart,
  isMajor,
  twentySevenYearsAgoStart,
} from "utils/helpers/date";
import { bindDateInputProps } from "utils/helpers/input";
import { DATE_FORMAT, RELATIONSHIP } from "utils/constants";
import Button from "../../Components/Button";
import Input from "../../Components/Input";
import Label from "../../Components/Label";

const BeneficiaryForm = ({
  values,
  formProps,
  addBeneficiaries,
  setAddBeneficiaries,
  childrenNumber,
  setChildrenNumber,
  translation,
  onSubmit,
}) => {
  const handleSpouseChange = ({ target: { checked } }) => {
    setAddBeneficiaries((prev) => ({
      ...prev,
      spouse: checked,
    }));
    if (checked) {
      formProps.setFieldValue("spouse", {
        id: cuid(),
        relationship: RELATIONSHIP.SPOUSE,
        identity: {
          id: cuid(),
          user: {
            id: cuid(),
            birthDate: null,
          },
        },
      });
    } else {
      formProps.setFieldValue("spouse", undefined);
    }
  };

  const handleChildrenChange = ({ target: { checked } }) => {
    if (!checked) {
      setChildrenNumber(0);
      formProps.setFieldValue("children", []);
    } else if (childrenNumber === 0) {
      setChildrenNumber(1);
    }
    setAddBeneficiaries((prev) => ({
      ...prev,
      children: checked,
    }));
  };

  const isSubmitDisabled = useMemo(
    () =>
      !isEmpty(formProps.errors) ||
      (childrenNumber === 0 && !addBeneficiaries.spouse),
    [formProps.errors, childrenNumber, addBeneficiaries.spouse],
  );

  const spouseBirthDate = get(values, "spouse.identity.user.birthDate");

  return (
    <>
      <div className="beneficiary_container--expand--row">
        <Input
          type="checkbox"
          label={translation.PRODUCTS.SPOUSE}
          checked={addBeneficiaries.spouse}
          onChange={handleSpouseChange}
        />
        <Input
          type="date"
          label={translation.BIRTH_DATE.LABEL}
          placeholder="JJ / MM / AAAA"
          format={DATE_FORMAT}
          disabled={!addBeneficiaries.spouse}
          showToday={false}
          {...bindDateInputProps({
            name: "spouse.identity.user.birthDate",
            values,
            ...formProps,
          })}
          defaultPickerValue={!spouseBirthDate && dayjs().subtract(24, "years")}
          disabledDate={(current) =>
            current > isMajor || current < hundredYearsAgoStart
          }
        />
      </div>
      <div className="divider" />
      <div className="beneficiary_container--expand--row">
        <Input
          type="checkbox"
          label={translation.PRODUCTS.CHILDREN}
          checked={addBeneficiaries.children}
          onChange={handleChildrenChange}
        />
        <div className="children-count">
          <Button
            type="primary"
            className="children-count__button"
            onClick={() => setChildrenNumber((prev) => Math.max(0, prev - 1))}
            disabled={childrenNumber < 2 || !addBeneficiaries.children}
          >
            <FaMinus size={14} />
          </Button>
          <div className="flex--grow-1">
            <Input
              type="number"
              min={0}
              max={12}
              placeholder="0"
              name="gender"
              value={childrenNumber}
              onChange={({ target: { value } }) => {
                const numValue = Math.max(0, Math.min(12, +value || 0));
                setChildrenNumber(numValue);
              }}
              disabled={!addBeneficiaries.children}
            />
          </div>
          <Button
            type="primary"
            className="children-count__button"
            onClick={() => setChildrenNumber((prev) => Math.min(12, prev + 1))}
            disabled={childrenNumber > 11 || !addBeneficiaries.children}
          >
            <FaPlus size={14} />
          </Button>
        </div>
      </div>
      <div className="children-birthday">
        {Array.from({ length: childrenNumber }, (_, i) => (
          <div key={`child-${i}`} className="children-birthday--row">
            <Label
              label={`${
                translation.NUMBERS_MAPPING[i + 1] || i + 1
              } ${translation.CHILDREN_BIRTHDATES.CHILD}`}
            />
            <Input
              label={translation.BIRTH_DATE.LABEL}
              placeholder="JJ / MM / AAAA"
              type="date"
              variant="simple"
              format={DATE_FORMAT}
              disabledDate={(current) =>
                current > dayjs() || current < twentySevenYearsAgoStart
              }
              defaultPickerValue={
                !get(values, `children.${i}.identity.user.birthDate`) &&
                dayjs().subtract(1, "years")
              }
              showToday={false}
              {...bindDateInputProps({
                name: `children.${i}.identity.user.birthDate`,
                values,
                ...formProps,
              })}
            />
          </div>
        ))}
      </div>
      <Button
        block
        type="primary"
        onClick={onSubmit}
        disabled={isSubmitDisabled}
      >
        {translation.PRODUCTS.SUBMIT}
      </Button>
    </>
  );
};

export default BeneficiaryForm;
